import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./DragNDrop.css";
import MapContext from "../MapContext";
import { KML } from "ol/format.js";
import { useSelector } from "react-redux";
import { Vector as VectorSource } from "ol/source";
import VectorLayer from "ol/layer/Vector.js";
import { Button } from "@progress/kendo-react-buttons";
import { inAndOut } from "ol/easing";

const DragNDrop = () => {
    const { map } = useContext(MapContext);
    const dropBox = useRef();
    const projection = useSelector((state) => state.configData.mapConfig.map.projection);
    const [invalidData, setInvalidData] = useState(false);
    const [layerExists, setLayerExists] = useState(false);
    const [layerArray, setLayerArray] = useState([]);

    const handleDragEnd = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e?.nativeEvent?.dataTransfer?.files;
        // If multiple files are dropped, take just the first
        const file = files?.item(0);
        // Make sure file is kml
        if (file?.name.toLowerCase().includes(".kml")) {
            const DisplayName = file.name.slice(0, -4);
            // Make sure file has not been added already
            if (layerArray.includes(DisplayName)) {
                setLayerExists(true);
            }
            // Read file and and read features
            const reader = new FileReader();
            reader.onload = (e) => {
                // TextDecoder is necessary when read as array buffer
                const text = new TextDecoder().decode(e.target?.result);
                const kml = new KML({ extractStyles: true });
                try {
                    const features = kml.readFeatures(text, {
                        featureProjection: projection,
                    });
                    addDropLayer(map, DisplayName, features);
                    setLayerArray(() => [...layerArray, DisplayName]);
                } catch (error) {
                    console.error(error);
                    setInvalidData(true);
                }
                const dropLayer = getDropLayerByName(map, DisplayName);
                const extent = dropLayer?.getSource().getExtent();
                map.getView().fit(extent, {
                    padding: [80, 80, 80, 80],
                    easing: inAndOut,
                    duration: 750,
                });
            };
            // Reading as array buffer is best for large files
            reader.readAsArrayBuffer(file);
        } else {
            setInvalidData(true);
        }
    };

    const clearData = () => {
        const dropLayerNames = getDropLayers(map).map((l) => {
            return l.values_.DisplayName;
        });
        dropLayerNames.forEach((ln) => {
            const layer = getDropLayerByName(map, ln);
            map.removeLayer(layer);
        });
        setLayerArray(() => []);
    };

    const removeLayer = (l) => {
        const layer = getDropLayerByName(map, l);
        map.removeLayer(layer);
        setLayerArray(() => layerArray.filter((name) => name !== l));
    };

    useLayoutEffect(() => {
        const dropLayers = getDropLayers(map);
        const layerNames = dropLayers.map((dl) => dl.values_.DisplayName);
        setLayerArray(layerNames);
    }, []);

    useEffect(() => {
        if (!layerExists) return;
        setTimeout(() => setLayerExists(false), 2000);
    }, [layerExists]);

    useEffect(() => {
        if (!invalidData) return;
        setTimeout(() => setInvalidData(false), 2000);
    }, [invalidData]);

    return (
        <>
            <div className="tiitle">
                <h3>Add Layer</h3>
            </div>
            <div
                className="drop-box"
                ref={dropBox}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}>
                <div className="drop-text">+ Add KML File</div>
            </div>
            <div className="drop-box-error">
                {" "}
                {invalidData && <div>Invalid Data</div>}
                {layerExists && (
                    <div>
                        A layer with the same name has already been added to the map{" "}
                    </div>
                )}
            </div>
            <div className="drop-layer-header">
                {" "}
                <div>Layers:</div>
                <Button
                    onClick={clearData}
                    fillMode="outline"
                    className="drop-layer-remove-all"
                    disabled={layerArray.length === 0}>
                    Remove All
                </Button>
            </div>
            {layerArray.map((l) => {
                return (
                    <div className="drop-layer" key={l}>
                        <div>{l}</div>
                        <Button
                            icon="close"
                            fillMode="flat"
                            title="remove layer"
                            onClick={() => removeLayer(l)}
                        />
                    </div>
                );
            })}
        </>
    );
};

export default DragNDrop;

const addDropLayer = (map, DisplayName, features) => {
    const source = new VectorSource();
    const drawLayer = new VectorLayer({
        source: source,
        properties: { Id: "dropLayer", DisplayName: DisplayName },
    });
    source.addFeatures(features);
    map.addLayer(drawLayer);
};

const getDropLayers = (map) => {
    return map
        .getLayers()
        .getArray()
        .filter((layer) => layer.values_.Id === "dropLayer");
};

const getDropLayerByName = (map, DisplayName) => {
    const layer = getDropLayers(map).find((l) => l.values_.DisplayName === DisplayName);
    return layer;
};
